/* ═══════════════════════════════════════════════════════════════════════════
   rfhq_dom_observer.js  —  single shared MutationObserver for the whole page

   animations.js, perf.js, image_zoom_loader.js and image_zoom_hover.js all
   need to react to DOM nodes injected after load (webshop's ProductGrid,
   RFHQ carousels, portal lists…). Running one document-wide observer each
   means every mutation fans out to 4-5 callbacks, each doing its own
   querySelectorAll sweep — a compounding main-thread cost on exactly the
   pages that mutate most (product grids re-rendering on filter/sort/page).

   This file owns the one observer and hands subscribers a deduplicated,
   rAF-batched list of roots instead: mutations are collected as they
   happen, and all handlers run once per animation frame with nested nodes
   collapsed into their outermost batched ancestor. Subscribers therefore
   see each injected subtree exactly once, at most one frame after insert.

   API (available to any script loaded after this one):
     window.rfhqDom.onAdded(fn)    fn(addedRoots: Element[])  once per flush
     window.rfhqDom.onRemoved(fn)  fn(removedNodes: Element[])
     window.rfhqDom.onAttr(fn)     fn(targets: Element[])  — data-zoom-enabled
                                   changes only (the single attribute any
                                   subscriber currently cares about)
     window.rfhqDom.off(fn)        unsubscribe from every list
   ═══════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  if (window.rfhqDom) return;

  var addedHandlers   = [];
  var removedHandlers = [];
  var attrHandlers    = [];

  var pendingAdded   = [];
  var pendingRemoved = [];
  var pendingAttrs   = [];
  var frame = null;

  function flush() {
    frame = null;

    /* Collapse nested inserts: a node whose ancestor is also in the batch
       will be swept when the ancestor is scanned — delivering both would
       make subscribers process the same subtree twice. */
    var roots = pendingAdded.filter(function (node, i) {
      return !pendingAdded.some(function (other, j) {
        return j !== i && other !== node && other.contains(node);
      });
    });
    var removed = pendingRemoved;
    var attrs   = pendingAttrs;
    pendingAdded = []; pendingRemoved = []; pendingAttrs = [];

    if (roots.length)   addedHandlers.slice().forEach(function (fn)   { try { fn(roots);   } catch (_) {} });
    if (removed.length) removedHandlers.slice().forEach(function (fn) { try { fn(removed); } catch (_) {} });
    if (attrs.length)   attrHandlers.slice().forEach(function (fn)    { try { fn(attrs);   } catch (_) {} });
  }

  function schedule() {
    if (frame !== null) return;
    frame = window.requestAnimationFrame
      ? requestAnimationFrame(flush)
      : setTimeout(flush, 16);
  }

  function start() {
    var mo = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.type === 'attributes') {
          if (pendingAttrs.indexOf(m.target) === -1) pendingAttrs.push(m.target);
          continue;
        }
        for (var a = 0; a < m.addedNodes.length; a++) {
          var added = m.addedNodes[a];
          if (added.nodeType === 1 && pendingAdded.indexOf(added) === -1) pendingAdded.push(added);
        }
        for (var r = 0; r < m.removedNodes.length; r++) {
          if (m.removedNodes[r].nodeType === 1) pendingRemoved.push(m.removedNodes[r]);
        }
      }
      if (pendingAdded.length || pendingRemoved.length || pendingAttrs.length) schedule();
    });
    mo.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-zoom-enabled'],
    });
  }

  window.rfhqDom = {
    onAdded:   function (fn) { addedHandlers.push(fn); },
    onRemoved: function (fn) { removedHandlers.push(fn); },
    onAttr:    function (fn) { attrHandlers.push(fn); },
    off: function (fn) {
      [addedHandlers, removedHandlers, attrHandlers].forEach(function (list) {
        var i = list.indexOf(fn);
        if (i !== -1) list.splice(i, 1);
      });
    },
  };

  if (document.body) {
    start();
  } else {
    document.addEventListener('DOMContentLoaded', start);
  }

}());
